/**
 * CAPA lifecycle — updated business workflow.
 * Open -> Under Investigation -> Under RCA -> Action Planning
 * -> Approved -> Implementation -> Verification -> Closure (last stepper step; record status → Closed)
 */

import { stripHtml } from "../../../utils/avatarUtils";

/** Stored on CAPA row/detail as `Status` (exact strings). */
export const CAPA_STAGES = [
  "Open",
  "Under Investigation",
  "Under RCA",
  "Action Planning",
  "Approved",
  "Implementation",
  "Verification",
  "Closure",
  "Closed",
];

export const CLOSED_PHASE = "Closed";

/** New CAPAs always begin as Open. */
export const INITIAL_CAPA_STATUS = CAPA_STAGES[0];

/**
 * View-page stepper: labels, workflow `status`, and Font Awesome `fas` icon suffix.
 */
export const CAPA_STEPPER_STEPS = [
  { label: "Details", status: "Open", icon: "file-alt" },
  { label: "Investigation", status: "Under Investigation", icon: "search" },
  { label: "Under RCA", status: "Under RCA", icon: "project-diagram" },
  { label: "Action Planning", status: "Action Planning", icon: "tasks" },
  { label: "Approval", status: "Approved", icon: "thumbs-up" },
  { label: "Implementation", status: "Implementation", icon: "play-circle" },
  { label: "Verification", status: "Verification", icon: "clipboard-check" },
  { label: "Closure", status: "Closure", icon: "flag-checkered" },
];

/**
 * Title + non-empty description (HTML stripped) — unlocks Investigation in the stepper when Open.
 * @param {Record<string, unknown> | null} detail
 */
export function capaDetailsAreComplete(detail) {
  if (!detail) return false;
  const t = String(detail.Title || "").trim();
  if (!t) return false;
  return Boolean(stripHtml(String(detail.Description || "")).trim());
}

/** Demo QA reviewer StaffId (legacy sample data). */
export const DEMO_QA_REVIEWER_STAFF_ID =
  "cccccccc-cccc-cccc-cccc-cccccccccccc";

export const WORKFLOW_ACTIONS = {
  SUBMIT_INVESTIGATION: "submit_investigation",
  SUBMIT_RCA: "submit_rca",
  SUBMIT_ACTION_PLAN: "submit_action_plan",
  APPROVE: "approve",
  REJECT: "reject",
  MARK_COMPLETED: "mark_completed",
  /** Implementation stage → Verification (after every task is Completed, Partially Completed, or Deferred). */
  SEND_TO_VERIFICATION: "send_to_verification",
  SUBMIT_VERIFICATION_EFFECTIVE: "submit_verification_effective",
  SUBMIT_VERIFICATION_NOT_EFFECTIVE: "submit_verification_not_effective",
  /** Verification → Closure (after verification is complete). */
  SEND_TO_CLOSURE: "send_to_closure",
  /** Closure → Closed (final remarks required; auth on UI). */
  COMPLETE_CLOSURE: "complete_closure",
};

const OPEN = CAPA_STAGES[0];
const UNDER_INVESTIGATION = CAPA_STAGES[1];
const UNDER_RCA = CAPA_STAGES[2];
const ACTION_PLANNED = CAPA_STAGES[3];
const APPROVED = CAPA_STAGES[4];
/** Stored `CAPA.Status` for the implementation phase (after Approval). */
const IMPLEMENTATION = CAPA_STAGES[5];
/** @deprecated Older rows / audit may still use this exact string. */
export const LEGACY_CAPA_IMPLEMENTATION_STATUS = "In Progress";

/**
 * @param {string | undefined | null} status
 * @returns {boolean}
 */
export function isCapaImplementationPhase(status) {
  const s = String(status || "").trim();
  return s === IMPLEMENTATION || s === LEGACY_CAPA_IMPLEMENTATION_STATUS;
}

/**
 * Normalize legacy status label for UI (audit log, badges keyed by lowercase, etc.).
 * @param {string | undefined | null} status
 * @returns {string}
 */
export function normalizeCapaLifecycleStatusForUi(status) {
  const s = String(status || "").trim();
  if (s === LEGACY_CAPA_IMPLEMENTATION_STATUS) return IMPLEMENTATION;
  return s;
}

/**
 * Keys used to detect whether a config status label is in use by live CAPA rows.
 * Config may show "Investigation" while records store "Under Investigation".
 */
export function expandCapaStatusUsageKeys(status) {
  const raw = String(status || "").trim();
  if (!raw) return [];
  const normalized = normalizeCapaLifecycleStatusForUi(raw);
  const keys = new Set([raw.toLowerCase(), normalized.toLowerCase()]);

  CAPA_STEPPER_STEPS.forEach((step) => {
    const statusKey = String(step.status || "").toLowerCase();
    const labelKey = String(step.label || "").toLowerCase();
    if (keys.has(statusKey) || keys.has(labelKey)) {
      keys.add(statusKey);
      keys.add(labelKey);
    }
  });

  if (keys.has("under investigation")) keys.add("investigation");
  if (keys.has("investigation")) keys.add("under investigation");
  if (keys.has("under rca")) keys.add("rca");
  if (keys.has("rca")) keys.add("under rca");
  if (keys.has("approved")) keys.add("approval");
  if (keys.has("approval")) keys.add("approved");
  if (keys.has("in progress")) keys.add("implementation");

  return [...keys];
}

/**
 * Keys used to detect whether a config RCA method label is in use by live CAPA rows.
 * Config may show "5 Whys" while older records store "5_whys".
 */
export function expandCapaRcaMethodUsageKeys(method, optionList = []) {
  const raw = String(method || "").trim();
  if (!raw) return [];
  const keys = new Set([raw.toLowerCase()]);

  const extraAliases = [
    ["5_whys", "5 whys", "5-whys"],
    ["fishbone", "fishbone (ishikawa)", "ishikawa"],
    ["pareto", "pareto analysis", "pareto analysis (80/20)"],
    ["fta", "fault tree analysis", "fault tree analysis (fta)"],
    ["fmea"],
    ["dmaic"],
    ["affinity", "affinity diagram"],
    ["scatter", "scatter diagram"],
    ["brainstorming"],
    ["other"],
  ];

  const groups = [
    ...extraAliases,
    ...optionList.map((opt) => [
      String(opt?.value || "").trim().toLowerCase(),
      String(opt?.label || "").trim().toLowerCase(),
    ]),
  ];

  groups.forEach((group) => {
    const members = group.map((v) => String(v || "").trim().toLowerCase()).filter(Boolean);
    if (members.some((m) => keys.has(m))) {
      members.forEach((m) => keys.add(m));
    }
  });

  return [...keys];
}
const VERIFICATION = CAPA_STAGES[6];
const CLOSURE = CAPA_STAGES[7];
const CLOSED = CAPA_STAGES[8];

/**
 * Stepper index (0..CAPA_STEPPER_STEPS.length-1). Record status `Closed` maps to the
 * same step as `Closure` so the last stepper step is “Closure” only.
 * @param {string} status
 */
export function getStageIndex(status) {
  if (status === "Closed") {
    return CAPA_STAGES.indexOf("Closure");
  }
  const normalized = normalizeCapaLifecycleStatusForUi(status);
  const i = CAPA_STAGES.indexOf(normalized);
  return i >= 0 ? i : 0;
}

/**
 * @param {string} currentStatus
 * @returns {string|null} Label for primary advance button
 */
export function getAdvanceButtonLabel(currentStatus) {
  if (currentStatus === OPEN) return "Submit Investigation";
  if (currentStatus === UNDER_INVESTIGATION) return "Submit RCA";
  if (currentStatus === UNDER_RCA) return "Submit Action Plan";
  if (currentStatus === APPROVED) return "Mark as Completed";
  return "Continue";
}

function matchesStaff(field, staffId) {
  if (!staffId) return false;
  if (field === "__CURRENT_USER__") return true;
  return (
    field &&
    String(field).toLowerCase() === String(staffId).toLowerCase()
  );
}

/**
 * @param {object} detail — OwnerStaffId, CreatedByStaffId, ReviewerStaffId
 * @param {string} [staffId]
 */
export function getCapaRoles(detail, staffId) {
  const sid = staffId ? String(staffId).toLowerCase() : "";
  const owner = matchesStaff(detail.OwnerStaffId, staffId);
  const creator = matchesStaff(detail.CreatedByStaffId, staffId);
  const reviewerId = detail.ReviewerStaffId
    ? String(detail.ReviewerStaffId).toLowerCase()
    : "";
  const reviewer =
    (reviewerId && reviewerId === sid) ||
    String(DEMO_QA_REVIEWER_STAFF_ID).toLowerCase() === sid;

  return {
    isOwner: Boolean(owner),
    isCreator: Boolean(creator),
    isReviewer: Boolean(reviewer),
  };
}

/**
 * Workflow stage actions — CAPA owner or System Administrator only.
 * Task accept/reject is handled per assignee on task rows (not here).
 * @param {object} p
 * @param {string} p.status
 * @param {object} p.roles — getCapaRoles()
 * @param {boolean} p.isSystemAdmin
 */
export function getAvailableWorkflowActions({
  status,
  roles,
  isSystemAdmin,
}) {
  const canDriveStage = isSystemAdmin || roles.isOwner;
  const out = [];

  if (status === OPEN && canDriveStage) {
    out.push(WORKFLOW_ACTIONS.SUBMIT_INVESTIGATION);
  }
  if (status === UNDER_INVESTIGATION && canDriveStage) {
    out.push(WORKFLOW_ACTIONS.SUBMIT_RCA);
  }
  if (status === UNDER_RCA && canDriveStage) {
    out.push(WORKFLOW_ACTIONS.SUBMIT_ACTION_PLAN);
  }
  if (status === ACTION_PLANNED && canDriveStage) {
    out.push(WORKFLOW_ACTIONS.APPROVE, WORKFLOW_ACTIONS.REJECT);
  }
  if (status === APPROVED && canDriveStage) {
    out.push(WORKFLOW_ACTIONS.MARK_COMPLETED);
  }
  if (isCapaImplementationPhase(status) && canDriveStage) {
    out.push(WORKFLOW_ACTIONS.SEND_TO_VERIFICATION);
  }
  if (isCapaImplementationPhase(status) && canDriveStage) {
    out.push(
      WORKFLOW_ACTIONS.SUBMIT_VERIFICATION_EFFECTIVE,
      WORKFLOW_ACTIONS.SUBMIT_VERIFICATION_NOT_EFFECTIVE,
    );
  }
  if (status === VERIFICATION && canDriveStage) {
    out.push(
      WORKFLOW_ACTIONS.SUBMIT_VERIFICATION_NOT_EFFECTIVE,
      WORKFLOW_ACTIONS.SEND_TO_CLOSURE,
    );
  }
  if (status === CLOSURE && canDriveStage) {
    out.push(WORKFLOW_ACTIONS.COMPLETE_CLOSURE);
  }

  return out;
}

/**
 * @param {string} action — WORKFLOW_ACTIONS.*
 * @param {string} currentStatus
 */
export function getNextStatusAfterAction(action, currentStatus) {
  if (action === WORKFLOW_ACTIONS.SUBMIT_INVESTIGATION && currentStatus === OPEN)
    return UNDER_INVESTIGATION;
  if (
    action === WORKFLOW_ACTIONS.SUBMIT_RCA &&
    currentStatus === UNDER_INVESTIGATION
  )
    return UNDER_RCA;
  if (
    action === WORKFLOW_ACTIONS.SUBMIT_ACTION_PLAN &&
    currentStatus === UNDER_RCA
  )
    return ACTION_PLANNED;
  if (action === WORKFLOW_ACTIONS.APPROVE && currentStatus === ACTION_PLANNED)
    return APPROVED;
  if (action === WORKFLOW_ACTIONS.REJECT && currentStatus === ACTION_PLANNED)
    return ACTION_PLANNED;
  if (action === WORKFLOW_ACTIONS.MARK_COMPLETED && currentStatus === APPROVED)
    return IMPLEMENTATION;
  if (
    action === WORKFLOW_ACTIONS.SEND_TO_VERIFICATION &&
    isCapaImplementationPhase(currentStatus)
  )
    return VERIFICATION;
  if (
    action === WORKFLOW_ACTIONS.SUBMIT_VERIFICATION_EFFECTIVE &&
    isCapaImplementationPhase(currentStatus)
  )
    return VERIFICATION;
  if (
    action === WORKFLOW_ACTIONS.SUBMIT_VERIFICATION_NOT_EFFECTIVE &&
    (isCapaImplementationPhase(currentStatus) || currentStatus === VERIFICATION)
  )
    return ACTION_PLANNED;
  if (
    action === WORKFLOW_ACTIONS.SEND_TO_CLOSURE &&
    currentStatus === VERIFICATION
  )
    return CLOSURE;
  if (
    action === WORKFLOW_ACTIONS.COMPLETE_CLOSURE &&
    currentStatus === CLOSURE
  )
    return CLOSED;

  return null;
}
